import { useMemo, useState } from "react";
import {
  Badge,
  DataState,
  PageHeader,
  Select,
  Table,
  formatDate,
  formatLapTime,
  type Column,
} from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface LapRow {
  id: string;
  driverId: string;
  driverName?: string | null;
  sessionId: string;
  sessionCar?: string | null;
  sessionTrack?: string | null;
  lapNumber: number;
  lapTimeMs: number;
  sector1Ms: number | null;
  sector2Ms: number | null;
  sector3Ms: number | null;
  isValid: boolean;
  isHidden: boolean;
  position: number | null;
  createdAt: string;
}

export function VenueLapTimes() {
  const { currentVenue } = useOrgContext();
  const laps = useQuery<LapRow[]>(
    currentVenue ? `/api/venues/${currentVenue.id}/lap-times` : null,
  );

  const [filterSessionId, setFilterSessionId] = useState("");

  const rows = laps.data ?? [];

  // Build the session filter options inline from the lap-times themselves —
  // matches the hub-ui pattern (Select a session to view its laps) without
  // requiring a separate sessions fetch.
  const sessionOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of rows) {
      if (!map.has(l.sessionId)) {
        const car = l.sessionCar ?? "";
        const track = l.sessionTrack ?? "";
        const label = [car, track].filter(Boolean).join(" — ") || `${l.sessionId.slice(0, 8)}…`;
        map.set(l.sessionId, label);
      }
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [rows]);

  const filtered = useMemo(
    () => (filterSessionId ? rows.filter((l) => l.sessionId === filterSessionId) : rows),
    [rows, filterSessionId],
  );

  const columns: Column<LapRow>[] = [
    {
      key: "createdAt",
      header: "When",
      render: (l) => <span className="text-sm text-gray-700">{formatDate(l.createdAt)}</span>,
    },
    {
      key: "driver",
      header: "Driver",
      render: (l) =>
        l.driverName ? (
          <span className="text-sm text-gray-900">{l.driverName}</span>
        ) : (
          <code className="text-xs text-gray-500">{l.driverId.slice(0, 8)}…</code>
        ),
    },
    {
      key: "lapNumber",
      header: "Lap #",
      render: (l) => <span className="text-sm font-medium text-gray-900">{l.lapNumber}</span>,
    },
    {
      key: "lapTime",
      header: "Lap Time",
      render: (l) => <span className="font-mono">{formatLapTime(l.lapTimeMs)}</span>,
    },
    {
      key: "s1",
      header: "S1",
      render: (l) =>
        l.sector1Ms ? (
          <span className="font-mono text-xs">{formatLapTime(l.sector1Ms)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "s2",
      header: "S2",
      render: (l) =>
        l.sector2Ms ? (
          <span className="font-mono text-xs">{formatLapTime(l.sector2Ms)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "s3",
      header: "S3",
      render: (l) =>
        l.sector3Ms ? (
          <span className="font-mono text-xs">{formatLapTime(l.sector3Ms)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "valid",
      header: "Valid",
      render: (l) => (
        <Badge className={l.isValid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
          {l.isValid ? "Valid" : "Invalid"}
        </Badge>
      ),
    },
  ];

  if (!currentVenue) return null;

  return (
    <div>
      <PageHeader title="Lap Times" subtitle="Valid laps at this venue (read-only mirror)." />

      <div className="flex gap-4 mb-4">
        <div className="w-80">
          <Select
            label="Session"
            placeholder="All sessions"
            options={sessionOptions}
            value={filterSessionId}
            onChange={(e) => setFilterSessionId(e.target.value)}
          />
        </div>
      </div>

      <DataState
        loading={laps.loading}
        error={laps.error}
        empty={!laps.loading && rows.length === 0}
        emptyMessage="No lap times recorded yet."
      >
        <>
          <p className="text-sm text-gray-500 mb-3">
            {filterSessionId
              ? `${filtered.length} of ${rows.length} laps`
              : `${rows.length} laps`}
          </p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <Table<LapRow>
              columns={columns}
              data={filtered}
              keyExtractor={(l) => l.id}
              rowClassName={(l) => (l.isHidden ? "bg-gray-50 opacity-60" : "")}
            />
          </div>
        </>
      </DataState>
    </div>
  );
}
